import { faker } from "@faker-js/faker";
import { GenderApiValues } from "@utils/constants";
import { getGenderValue, setGenderValue } from "@utils/helpers";
import { formatDateYYYY_MM_DD } from "@utils/utils";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

import { Button, Headline, Label, Paragraph, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ReportTypeEnums,
  UpdateReportClientDetailsMutationVariables,
  useUpdateReportClientDetailsMutation,
} from "@api/graphql/types-and-hooks";
import { ContentRow } from "@components/ContentRow/ContentRow";
import { DevOnlyWrapper } from "@components/DevOnlyWrapper/DevOnlyWrapper";
import { DoubleColumnContainer, FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError } from "@components/notifications/notifications";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";

export type ReportClientDetailsRef = {
  submitForm: () => Promise<boolean>;
};

export const ReportClientDetails = forwardRef<ReportClientDetailsRef>(({}, ref) => {
  const { reportType, isDiagnosisEvaluation } = useClinicalReportDetailsData();
  const { report, reportQuery } = useReport();
  const hasInitialized = useRef(false);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const flags = useFlags();

  const updateReportClientDetailsMutation = useUpdateReportClientDetailsMutation({});
  // mutateAsync gets a fresh identity every time the mutation settles, which
  // otherwise re-creates performAutosave -> re-arms the watchedValues debounce
  // timer -> a redundant autosave fires 2s after every successful save with no
  // user edits in between. Pin it in a ref so performAutosave's identity stays
  // stable across mutation settles.
  const mutateAsyncRef = useRef(updateReportClientDetailsMutation.mutateAsync);
  mutateAsyncRef.current = updateReportClientDetailsMutation.mutateAsync;

  const {
    onSubmit,
    formState,
    RegisteredFormInput,
    RegisteredFormSelected,
    reset,
    setValue,
    trigger,
    watch,
    getValues,
  } = useFormUtils<UpdateReportClientDetailsMutationVariables>({
    mutationFn: async () => {
      console.debug("This should not trigger — form submission is managed elsewhere.");
    },
  });

  const performAutosave = useCallback(async () => {
    if (!hasInitialized.current || !report?.id) return;

    if (!formState.isValid) return;

    const params = getValues();

    try {
      await mutateAsyncRef.current({
        reportId: report.id,
        learnerCaregiverMinimalInput: {
          ...params.learnerCaregiverMinimalInput,
          gender: setGenderValue(params.learnerCaregiverMinimalInput.gender),
        },
        reportWriterAndDate: params.reportWriterAndDate,
        previousReportDetails: reportType === ReportTypeEnums.Reassessment ? params.previousReportDetails : undefined,
      });
    } catch (error) {
      console.error("Autosave failed:", error);
    }
  }, [report?.id, reportType, getValues, formState.isValid]);

  // Watch all form values for changes
  const watchedValues = watch();

  // Debounced autosave effect
  useEffect(() => {
    if (!hasInitialized.current) return;

    // Clear existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    // Set new timeout for autosave
    autosaveTimeoutRef.current = setTimeout(() => {
      performAutosave();
    }, 2000); // Autosave after 2 seconds of inactivity

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [watchedValues, performAutosave]);

  // Initialize form only once when report data is first available
  useEffect(() => {
    if (report && !hasInitialized.current) {
      const formattedGender = getGenderValue(report?.reportClientDetails?.gender) || "";
      reset({
        learnerCaregiverMinimalInput: {
          clientFirstName: report?.reportClientDetails?.clientFirstName,
          clientLastName: report?.reportClientDetails?.clientLastName || "",
          birthDate: report?.reportClientDetails?.birthDate,
          gender: formattedGender,
          specifyGender: report?.reportClientDetails?.specifyGender || "",
          address: report?.reportClientDetails?.address || "",
          addressLine2: report?.reportClientDetails?.addressLine2,
          city: report?.reportClientDetails?.city,
          state: report?.reportClientDetails?.state,
          zip: report?.reportClientDetails?.zip,
          caregiverFirstName: report?.reportClientDetails?.guardianFirstName,
          caregiverLastName: report?.reportClientDetails?.guardianLastName || "",
          caregiver2FirstName: report?.reportClientDetails?.guardian2FirstName || "",
          caregiver2LastName: report?.reportClientDetails?.guardian2LastName || "",
        },
        reportWriterAndDate: {
          reportCompletionDate: report?.completionDate,
          reportWriter: report?.reportWriter,
        },
        previousReportDetails: {
          startDate: report.previousReportStartDate,
          endDate: report.previousReportEndDate,
          submittedDate: report.previousReportSubmittedDate,
        },
      });
      hasInitialized.current = true;
    }
  }, [report, reset]);

  const startDateWatch = watch("previousReportDetails.startDate");

  const genderWatch = watch("learnerCaregiverMinimalInput.gender");

  const isReadOnly = false;

  const getForm = () => (
    <>
      {!isReadOnly ? (
        <DevOnlyWrapper>
          <div className="my-4 flex items-center">
            <Button
              appearance="link"
              className="mr-2"
              text="Fill Fields (dev only)"
              onClick={() => {
                // Client details
                setValue("learnerCaregiverMinimalInput.clientFirstName", `(fake) ${faker.person.firstName()}`);
                setValue("learnerCaregiverMinimalInput.clientLastName", `(fake) ${faker.person.lastName()}`);
                setValue(`learnerCaregiverMinimalInput.birthDate`, format(faker.date.birthdate(), "yyyy-MM-dd"), {
                  shouldValidate: true,
                });
                setValue(
                  "learnerCaregiverMinimalInput.gender",
                  `${faker.helpers.arrayElement(["Boy/man", "Girl/woman"])}`,
                );
                setValue("learnerCaregiverMinimalInput.specifyGender", "");

                // Address details
                setValue("learnerCaregiverMinimalInput.address", `${faker.location.streetAddress()}`);
                setValue("learnerCaregiverMinimalInput.addressLine2", `${faker.location.secondaryAddress()}`);
                setValue("learnerCaregiverMinimalInput.city", `${faker.location.city()}`);
                setValue("learnerCaregiverMinimalInput.state", `${faker.location.state()}`);
                setValue("learnerCaregiverMinimalInput.zip", `${faker.location.zipCode()}`);

                // Guardian details
                setValue("learnerCaregiverMinimalInput.caregiverFirstName", `(fake) ${faker.person.firstName()}`);
                setValue("learnerCaregiverMinimalInput.caregiverLastName", `(fake) ${faker.person.lastName()}`);
                setValue("learnerCaregiverMinimalInput.caregiver2FirstName", `(fake) ${faker.person.firstName()}`);
                setValue("learnerCaregiverMinimalInput.caregiver2LastName", `(fake) ${faker.person.lastName()}`);

                // Report writer and date
                setValue(
                  "reportWriterAndDate.reportCompletionDate",
                  format(faker.date.recent({ days: 30 }), "yyyy-MM-dd"),
                );
                setValue("reportWriterAndDate.reportWriter", `Dr. ${faker.person.fullName()}`);

                // Previous report details (if reassessment)
                if (reportType === ReportTypeEnums.Reassessment) {
                  const endDate = faker.date.past({ years: 1 });
                  const startDate = new Date(endDate);
                  startDate.setFullYear(startDate.getFullYear() - 1); // 1 year before end date
                  const submittedDate = faker.date.between({ from: endDate, to: new Date() });

                  setValue("previousReportDetails.startDate", format(startDate, "yyyy-MM-dd"));
                  setValue("previousReportDetails.endDate", format(endDate, "yyyy-MM-dd"));
                  setValue("previousReportDetails.submittedDate", format(submittedDate, "yyyy-MM-dd"));
                }

                trigger();
              }}
            />
            <Button
              appearance="link"
              text="Clear Fields (dev only)"
              onClick={() => {
                reset();
                trigger();
              }}
            />
          </div>
        </DevOnlyWrapper>
      ) : null}
      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.clientFirstName"
            inputSize="full"
            formState={formState}
            label="Client's First Name"
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.clientLastName"
            inputSize="full"
            formState={formState}
            label="Client's Last Name"
            readOnly={isReadOnly}
          />
        </div>
      </div>
      <div className="gap-x-4 lg:grid lg:grid-cols-1">
        <RegisteredFormInput
          formKey="learnerCaregiverMinimalInput.birthDate"
          inputSize="full"
          type="date"
          formState={formState}
          max={formatDateYYYY_MM_DD(new Date())}
          label="Date of Birth"
          readOnly={isReadOnly}
        />
        <div />
      </div>
      {isReadOnly ? (
        <DoubleColumnContainer>
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.gender"
            formState={formState}
            label="Gender"
            readOnly
            inputSize="full"
          />
          {genderWatch === "Gender identity is not listed" && (
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.specifyGender"
              formState={formState}
              required={false}
              label="Specify Gender"
              readOnly
              inputSize="full"
            />
          )}
        </DoubleColumnContainer>
      ) : (
        <DoubleColumnContainer>
          <RegisteredFormSelected
            formKey="learnerCaregiverMinimalInput.gender"
            formState={formState}
            placeholderText={""}
            title="Gender"
            readOnly={isReadOnly}
            items={
              Object.values(GenderApiValues).map((o) => ({
                primary: getGenderValue(o),
              })) as SelectItem[]
            }
          />
          {genderWatch === "Gender identity is not listed" && (
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.specifyGender"
              formState={formState}
              required={false}
              label="Specify Gender"
              readOnly={isReadOnly}
            />
          )}
        </DoubleColumnContainer>
      )}
      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.address"
            inputSize="full"
            formState={formState}
            label="Address Line 1"
            required={false}
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.addressLine2"
            inputSize="full"
            required={false}
            formState={formState}
            label="Address Line 2"
            readOnly={isReadOnly}
          />
        </div>
      </div>
      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.city"
            inputSize="full"
            formState={formState}
            label="City"
            required={false}
            readOnly={isReadOnly}
          />

          <div className="gap-x-4  lg:grid lg:grid-cols-2">
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.state"
              inputSize="full"
              formState={formState}
              label="State / Province"
              required={false}
              readOnly={isReadOnly}
            />
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.zip"
              inputSize="full"
              formState={formState}
              label="ZIP / Postal Code"
              required={false}
              readOnly={isReadOnly}
            />
          </div>
        </div>
      </div>

      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiverFirstName"
            inputSize="full"
            formState={formState}
            label="Guardian's First Name"
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiverLastName"
            inputSize="full"
            formState={formState}
            label="Guardian's Last Name"
            readOnly={isReadOnly}
          />
        </div>

        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiver2FirstName"
            inputSize="full"
            formState={formState}
            label="Second Guardian's First Name"
            required={false}
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiver2LastName"
            inputSize="full"
            formState={formState}
            label="Second Guardian's Last Name"
            required={false}
            readOnly={isReadOnly}
          />
        </div>
      </div>
    </>
  );

  useImperativeHandle(ref, () => ({
    submitForm: async (): Promise<boolean> => {
      //run default onSubmit to update formState (isSubmitted, submitCount) and enable onChange validation
      onSubmit();

      const isValid = await trigger();
      if (!isValid) {
        return false;
      }
      if (!report?.id) {
        console.error("Cannot save client details: report is not loaded yet.");
        notifyError("Error Updating Client Details");
        return false;
      }
      const params = getValues();

      // PROA-968: a missing learner used to bail out here; the backend now
      // lazy-creates and attaches a sparse Learner inside
      // updateReportClientDetails (see frontera#3541), so we can submit
      // unconditionally and let reportQuery.refetch() surface the new learner
      // on the next render. The await must be wrapped in try/catch so the
      // return value actually reflects the outcome — the previous onSuccess /
      // onError callback returns were discarded by mutateAsync, leaving the
      // function always returning true on a server error.
      try {
        await updateReportClientDetailsMutation.mutateAsync({
          reportId: report.id,
          learnerCaregiverMinimalInput: {
            ...params.learnerCaregiverMinimalInput,
            gender: setGenderValue(params.learnerCaregiverMinimalInput.gender),
          },
          reportWriterAndDate: params.reportWriterAndDate,
          previousReportDetails: reportType === ReportTypeEnums.Reassessment ? params.previousReportDetails : undefined,
        });
        await reportQuery.refetch();
        return true;
      } catch (error) {
        console.error("Error updating client details", error);
        notifyError("Error Updating Client Details");
        return false;
      }
    },
  }));

  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <FormContainer onSubmit={onSubmit}>
        {reportType == ReportTypeEnums.Reassessment && (
          <div className="flex flex-col rounded-xl border border-border-strong bg-white p-6">
            <div className="flex flex-col mb-4">
              <Headline size="medium">Previous Assessment Report</Headline>
              <Paragraph size="small" className="text-text-tertiary">
                These dates help reviewers understand the timeline of prior services.
              </Paragraph>
            </div>
            <div className="col-span-3 flex flex-col">
              <Label displayType="loud" colorType="primary" className="mb-2">
                Authorization Period
              </Label>
              <div className="gap-x-4  lg:grid lg:grid-cols-2">
                <RegisteredFormInput
                  formKey="previousReportDetails.startDate"
                  inputSize="full"
                  type="date"
                  formState={formState}
                  max={formatDateYYYY_MM_DD(new Date())}
                  label="Start Date"
                  readOnly={isReadOnly}
                />
                <RegisteredFormInput
                  formKey="previousReportDetails.endDate"
                  inputSize="full"
                  type="date"
                  formState={formState}
                  min={formatDateYYYY_MM_DD(startDateWatch)}
                  label="End Date"
                  readOnly={isReadOnly}
                />
              </div>
              <div className="gap-x-4 lg:grid lg:grid-cols-1">
                <RegisteredFormInput
                  formKey="previousReportDetails.submittedDate"
                  inputSize="full"
                  type="date"
                  formState={formState}
                  max={formatDateYYYY_MM_DD(new Date())}
                  label="Date Previous Report Written"
                  readOnly={isReadOnly}
                />
              </div>
            </div>
          </div>
        )}
        {isDiagnosisEvaluation && flags["db-singlepage-bulkupload"] ? (
          <div className="flex flex-col rounded-xl border border-border-strong bg-white p-6">
            <div className="flex flex-col mb-4">
              <Headline size="medium">Client Details</Headline>
              <Paragraph size="small" className="text-text-tertiary">
                Please fill out and verify the following client information.{" "}
              </Paragraph>
            </div>
            {getForm()}
          </div>
        ) : reportType === ReportTypeEnums.Reassessment ? (
          <div className="flex flex-col rounded-xl border border-border-strong bg-white mt-4 p-6">
            <div className="flex flex-col mb-4">
              <Headline size="medium">Client Details</Headline>
            </div>
            <div className="flex w-full flex-col">{getForm()}</div>
          </div>
        ) : (
          <ContentRow title="Client Details" subtitle="">
            <div className="flex w-full flex-col ">{getForm()}</div>
          </ContentRow>
        )}
      </FormContainer>
    </>
  );
});
ReportClientDetails.displayName = "ReportClientDetails";
